import { useCallback, useRef, useState } from 'react';
import { Box, Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { formatVideoTime } from '@/types';

interface Props {
  value: number;
  onChange: (seconds: number) => void;
  duration?: number;
  dark?: boolean;
  onPreview?: (seconds: number) => void;
  label?: string;
}

function clampTime(seconds: number, duration?: number) {
  const max = duration && duration > 0 ? duration : Math.max(seconds, 0);
  return Math.min(Math.max(0, seconds), max);
}

function secondsToInput(sec: number) {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function parseVideoTime(input: string): number | undefined {
  const trimmed = input.trim();
  if (!trimmed) return undefined;
  if (trimmed.includes(':')) {
    const [m, s] = trimmed.split(':');
    const mins = parseInt(m, 10);
    const secs = parseInt(s, 10);
    if (Number.isFinite(mins) && Number.isFinite(secs)) return mins * 60 + secs;
  }
  const n = parseFloat(trimmed);
  return Number.isFinite(n) ? n : undefined;
}

const NUDGE_STEPS = [-10, -5, -1, 1, 5, 10] as const;

export default function VideoTimestampEditor({
  value,
  onChange,
  duration = 0,
  dark = false,
  onPreview,
  label = '影片時間',
}: Props) {
  const [inputMode, setInputMode] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const hasDuration = duration > 0;

  const setTime = useCallback(
    (next: number, preview = true) => {
      const clamped = clampTime(next, hasDuration ? duration : undefined);
      onChange(clamped);
      if (preview) onPreview?.(clamped);
    },
    [onChange, onPreview, duration, hasDuration]
  );

  const nudge = (delta: number) => setTime(value + delta);

  const openInput = () => {
    setDraft(secondsToInput(value));
    setInputMode(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const commitInput = () => {
    const parsed = parseVideoTime(draft);
    if (parsed != null) setTime(parsed);
    setInputMode(false);
  };

  const captionColor = dark ? 'rgba(255,255,255,0.55)' : 'text.secondary';
  const timeColor = dark ? '#fff' : 'text.primary';
  const fieldSx = dark
    ? {
        '& .MuiInputBase-root': { color: '#fff', fontWeight: 700, fontSize: '1.1rem' },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.28)' },
      }
    : { '& .MuiInputBase-root': { fontWeight: 700, fontSize: '1.1rem' } };

  return (
    <Box>
      <Typography variant="caption" color={captionColor} fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>

      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75 }}>
        {inputMode ? (
          <TextField
            inputRef={inputRef}
            size="small"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitInput}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitInput();
              if (e.key === 'Escape') setInputMode(false);
            }}
            placeholder="m:ss"
            sx={{ flex: 1, ...fieldSx }}
          />
        ) : (
          <>
            <Typography variant="h6" fontWeight={800} color={timeColor} sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
              {formatVideoTime(value)}
            </Typography>
            <Typography variant="caption" color={captionColor} sx={{ fontVariantNumeric: 'tabular-nums' }}>
              / {hasDuration ? formatVideoTime(duration) : '—'}
            </Typography>
            <IconButton size="small" aria-label="輸入時間" onClick={openInput} sx={iconSx(dark)}>
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </>
        )}
      </Stack>

      <Stack direction="row" spacing={0.5} justifyContent="stretch">
        {NUDGE_STEPS.map((step) => (
          <Button
            key={step}
            size="small"
            variant="outlined"
            onClick={() => nudge(step)}
            sx={{ flex: 1, minWidth: 0, px: 0.25, ...stepTextBtnSx(dark) }}
          >
            {step > 0 ? `+${step}` : step}
          </Button>
        ))}
      </Stack>
    </Box>
  );
}

function iconSx(dark: boolean) {
  return dark
    ? { color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.08)' } }
    : { color: 'text.secondary' };
}

function stepTextBtnSx(dark: boolean) {
  return {
    fontVariantNumeric: 'tabular-nums',
    fontSize: '0.72rem',
    fontWeight: 700,
    minHeight: 28,
    ...(dark
      ? {
          color: 'rgba(255,255,255,0.88)',
          borderColor: 'rgba(255,255,255,0.22)',
          '&:hover': { borderColor: 'rgba(255,255,255,0.4)', bgcolor: 'rgba(255,255,255,0.06)' },
        }
      : {}),
  } as const;
}
